import { ArrowUpTrayIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline'
import { cva, cx } from 'class-variance-authority'
import PropTypes from 'prop-types'
import { useId, useRef } from 'react'
import ExistingFile from 'ui-toolkit-tailwind/src/ExistingFile'

const variants = cva(
	/* base style */
	'block cursor-pointer w-full text-sm text-gray-900 file:cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-300 file:text-primary-900 hover:file:bg-gray-200 !outline-none',
	{
		variants: {
			invalid: {
				true: 'file:border-danger-600 file:bg-danger-100 file:text-danger-600 hover:file:bg-danger-200'
			},
			pill: {
				true: '!rounded-full px-4'
			},
			disabled: {
				true: 'file:cursor-not-allowed opacity-50 hover:file:bg-primary-300'
			}
		},
		defaultVariants: {}
	}
)

/**
 * @typedef FileUploadProps
 * @property {string} [className]
 * @property {string} [name]
 * @property {string} [label]
 * @property {React.ReactNode} [helperText] Helper text to display below the label (hide when there's error message)
 * @property {boolean} [disabled]
 * @property {boolean} [invalid]
 * @property {string} [errorMessage]
 * @property {boolean} [pill]
 * @property {File | ExistingFile} [value]
 * @property {(file: File | ExistingFile) => void} [onChange]
 * @property {string} endpointUrl required for viewing existing files
 */

/**
 * @param {FileUploadProps & React.InputHTMLAttributes} props
 */
const FileUpload = ({
	className,
	name,
	label,
	helperText,
	cornerHint,
	disabled,
	invalid,
	errorMessage,
	pill,
	value,
	onChange = () => {},
	endpointUrl,
	...props
}) => {
	const inputRef = useRef(/** @type {React.InputHTMLAttributes} */ (null))
	const randomId = useId()
	const descriptionId = `${name || randomId}-description`

	const hasLabel = !!label
	const hasError = invalid && errorMessage

	const existingFile = value instanceof ExistingFile ? value : null

	return (
		<div className="w-full">
			{hasLabel && (
				<div className={cx('flex justify-between', pill && 'px-4')}>
					<label htmlFor={name} className="block text-sm font-medium leading-6 text-gray-900">
						{label}
					</label>
					{cornerHint && (
						<span className="text-xs leading-6 text-gray-500" id={descriptionId}>
							{cornerHint}
						</span>
					)}
				</div>
			)}

			{existingFile && (
				<div className="flex justify-between gap-1">
					{existingFile.viewUrl && endpointUrl ? (
						<a
							className="flex items-center gap-1 text-xs truncate w-fit hover:underline hover:text-primary-600"
							href={`${endpointUrl}${existingFile.viewUrl}`}
							target="_blank"
							rel="noreferrer"
						>
							<EyeIcon className="size-3" />
							{existingFile.filename}
						</a>
					) : (
						<span className="flex items-center gap-1 text-xs truncate w-fit">
							<EyeIcon className="size-3" />
							{existingFile.filename}
						</span>
					)}
					{!disabled && (
						<div className="flex gap-1">
							<button
								type="button"
								className="flex items-center gap-1 px-2 py-1 text-gray-600 border border-gray-300 rounded-md hover:text-gray-700 hover:bg-gray-50"
								onClick={() => {
									const inputElement = inputRef.current
									if (inputElement) {
										inputElement.click()
									}
								}}
							>
								<ArrowUpTrayIcon className="size-3" />
								<span className="hidden text-xs md:inline">Upload</span>
							</button>
							<button
								type="button"
								className="flex items-center gap-1 px-2 py-1 text-gray-600 border border-gray-300 rounded-md hover:text-gray-700 hover:bg-gray-50"
								onClick={() => {
									const confirmed = window.confirm('Are you sure you want to remove this file?')
									if (confirmed) {
										onChange(null)
									}
								}}
							>
								<TrashIcon className="size-3" />
								<span className="hidden text-xs md:inline">Remove</span>
							</button>
						</div>
					)}
				</div>
			)}

			<label className={cx('block', hasLabel && 'mt-2', existingFile && 'hidden')}>
				<span className="sr-only">Choose file</span>
				<input
					ref={inputRef}
					className={variants({ invalid, pill, disabled })}
					type="file"
					disabled={disabled}
					aria-invalid={invalid}
					aria-describedby={descriptionId}
					onChange={e => onChange(e.target.files[0])}
					{...props}
				/>
			</label>

			{/* hide helper text when there's error message */}
			{helperText && !hasError && (
				<p className={cx('mt-2 text-sm text-gray-500', pill && 'px-4')} id={descriptionId}>
					{helperText}
				</p>
			)}

			{hasError && (
				<p className={cx('mt-2 text-sm text-danger-600', pill && 'px-4')} id={descriptionId}>
					{errorMessage}
				</p>
			)}
		</div>
	)
}

FileUpload.propTypes = {
	className: PropTypes.string,
	name: PropTypes.string,
	label: PropTypes.node,
	helperText: PropTypes.string,
	cornerHint: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	disabled: PropTypes.bool,
	invalid: PropTypes.bool,
	errorMessage: PropTypes.string,
	pill: PropTypes.bool,
	value: PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.instanceOf(ExistingFile)]),
	onChange: PropTypes.func,
	endpointUrl: PropTypes.string
}

FileUpload.defaultProps = {}

export default FileUpload
